// src/App.js
import React, { useState, useEffect } from 'react';
import { 
    LayoutDashboard, FolderOpen, Calendar, Map, BookOpen, 
    CheckSquare, Users, ShoppingBag, Briefcase, Gamepad2, 
    Mail, Menu, X, LogOut, Info, FileText, MessageSquare, AlertCircle
} from 'lucide-react';

// --- Firebase Imports ---
import { doc, onSnapshot, collection, addDoc, serverTimestamp } from 'firebase/firestore'; 
import { db } from './firebase'; 
import { AuthProvider, useAuth } from './context/AuthContext';

// --- Import Pages ---
import LandingPage from './pages/LandingPage'; 
import Dashboard from './pages/Dashboard';
import Portfolio from './pages/Portfolio'; 
import Events from './pages/Events';
import Pathway from './pages/Pathway';
import Jobs from './pages/Jobs';
import PersonalDev from './pages/PersonalDev';
import TaskBoard from './pages/TaskBoard'; 
import Network from './pages/Network';
import Marketplace from './pages/Marketplace'; 
import Contact from './pages/Contact'; 
import Medle from './pages/Medle';

function AppContent() {
    const [activeView, setActiveView] = useState('dashboard');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // NEW: Mobile menu state
    const { currentUser, logout } = useAuth();
    const [userProfile, setUserProfile] = useState(null); 
    const [feedbackSent, setFeedbackSent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (currentUser) {
            const unsub = onSnapshot(doc(db, "users", currentUser.uid), (docSnapshot) => {
                if (docSnapshot.exists()) setUserProfile(docSnapshot.data());
                else setUserProfile({ name: currentUser.displayName || '', email: currentUser.email });
            });
            return () => unsub();
        }
    }, [currentUser]);

    const handleLogout = async () => { 
        try { await logout(); } catch (error) { console.error("Logout failed", error); } 
    };

    const getDisplayName = () => userProfile?.name || userProfile?.displayName || currentUser.email?.split('@')[0] || 'User';
    const getInitials = () => {
        let name = getDisplayName().trim();
        const parts = name.split(' ');
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return parts[0] ? parts[0][0].toUpperCase() : 'U';
    };

    const handleFeedbackSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const formData = new FormData(e.target);
        const type = formData.get('type');
        const details = formData.get('details');
        const file = formData.get('userFile'); 

        try {
            let attachments = [];
            if (file && file.size > 0) {
                const base64String = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = () => resolve(reader.result.split(',')[1]);
                    reader.onerror = (error) => reject(error);
                });

                attachments.push({
                    filename: file.name,
                    content: base64String,
                    encoding: 'base64'
                });
            }

            await addDoc(collection(db, "mail"), {
                to: "craigmacvicar80@gmail.com",
                message: {
                    subject: `[Feedback] ${type} from ${getDisplayName()}`,
                    text: `User: ${getDisplayName()} (${currentUser.email})\nType: ${type}\n\nDetails:\n${details}`,
                    attachments: attachments
                },
                timestamp: serverTimestamp()
            });
            
            setFeedbackSent(true);
        } catch (error) {
            console.error("Error sending feedback:", error);
            alert("Failed to send feedback. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!currentUser) return <LandingPage />;

    const navItems = [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'portfolio', label: 'Portfolio', icon: FolderOpen },
        { id: 'jobs', label: 'Jobs', icon: Briefcase },
        { id: 'events', label: 'Events', icon: Calendar },
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        { id: 'network', label: 'Network', icon: Users },
        { id: 'pathway', label: 'Pathway', icon: Map },
        { id: 'development', label: 'Personal Dev', icon: BookOpen },
        { id: 'marketplace', label: 'Shop', icon: ShoppingBag },
        { id: 'contact', label: 'Contact', icon: Mail },
        { id: 'medle', label: 'Medle', icon: Gamepad2, separate: true },
    ];

    const renderContent = () => {
        switch (activeView) {
            case 'dashboard': return <Dashboard setActiveView={setActiveView} />;
            case 'portfolio': return <Portfolio activities={[]} />;
            case 'events': return <Events />;
            case 'pathway': return <Pathway />;
            case 'jobs': return <Jobs />;
            case 'development': return <PersonalDev />;
            case 'tasks': return <TaskBoard />;
            case 'network': return <Network />;
            case 'medle': return <Medle />;
            case 'marketplace': return <Marketplace />;
            case 'contact': return <Contact />; 
            
            case 'feedback': return (
                <div className="p-4 md:p-8 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <h1 className="text-2xl md:text-3xl font-bold mb-2">Feedback & Requests</h1>
                    <p className="text-slate-500 mb-8">Have a feature request or found a bug? Let us know below.</p>
                    
                    {feedbackSent ? (
                        <div className="bg-emerald-50 text-emerald-700 p-6 rounded-2xl border border-emerald-100 flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4 text-emerald-600 font-bold">✓</div>
                            <h3 className="font-bold text-lg">Thank You!</h3>
                            <p>Your feedback and attachment have been sent successfully.</p>
                            <button onClick={() => setFeedbackSent(false)} className="mt-4 text-emerald-600 font-medium underline">Send another</button>
                        </div>
                    ) : (
                        <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200">
                            <form onSubmit={handleFeedbackSubmit} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
                                    <select name="type" required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500">
                                        <option>Feature Request (I want...)</option>
                                        <option>Bug Report (Something's broken)</option>
                                        <option>Surgical Content Idea</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Details</label>
                                    <textarea name="details" required rows="5" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" placeholder="Describe what you need or what happened..."></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Attach Screenshot or File (Optional)</label>
                                    <input 
                                        type="file" 
                                        name="userFile"
                                        accept="image/*,application/pdf"
                                        className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    />
                                </div>
                                <button 
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full py-4 bg-blue-600 text-white rounded-xl font-bold transition-all shadow-lg shadow-blue-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                                >
                                    {isSubmitting ? 'Sending...' : 'Submit Feedback'}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            );

            case 'privacy': return (
                <div className="p-4 md:p-8 max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <h1 className="text-2xl md:text-3xl font-bold mb-6 text-slate-800">Privacy Policy</h1>
                    <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6 text-slate-600">
                        <section>
                            <h3 className="text-lg font-bold text-slate-800 mb-2">1. Data Security</h3>
                            <p>Your surgical career data is tied to your unique Google/Email UID. We use Firebase's industry-standard encryption to ensure your portfolio remains private and accessible only to you.</p>
                        </section>
                        <section>
                            <h3 className="text-lg font-bold text-slate-800 mb-2">2. Personal Information</h3>
                            <p>We only store information necessary for portfolio tracking: your name, grade, and logged activities. We do not share your data with third-party recruitment agencies.</p>
                        </section>
                        <section className="bg-red-50 p-4 rounded-xl border border-red-100">
                            <h3 className="text-lg font-bold text-red-800 mb-2">3. Patient Confidentiality</h3>
                            <p className="text-red-700 font-medium italic">Strict Warning: Do not upload patient-identifiable information (NHS numbers, names, or full DOB) in descriptions or attachments.</p>
                        </section>
                    </div>
                </div>
            );

            case 'disclaimer': return (
                <div className="p-4 md:p-8 max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <h1 className="text-2xl md:text-3xl font-bold mb-6 text-slate-800">Disclaimer</h1>
                    <div className="bg-amber-50 p-6 md:p-8 border-l-8 border-amber-500 rounded-r-2xl shadow-sm space-y-4">
                        <div className="flex items-center gap-3 text-amber-700 mb-2">
                            <AlertCircle size={24} />
                            <h3 className="text-xl font-bold">Educational Aid Only</h3>
                        </div>
                        <p className="text-amber-900 font-medium text-lg">This application is NOT an official surgical logbook.</p>
                        <ul className="list-disc ml-6 space-y-2 text-amber-800">
                            <li>Points calculations are estimates based on published ST3 specifications and are subject to change.</li>
                            <li>Users must verify final portfolio scores against official recruitment guidelines (JCST/HEE).</li>
                            <li>The developer assumes no liability for recruitment outcomes.</li>
                        </ul>
                    </div>
                </div>
            );

            case 'quick-start': return (
                <div className="p-4 md:p-8 max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <h1 className="text-2xl md:text-3xl font-bold mb-6 text-slate-800">Quick Start Guide</h1>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mb-4">1</div>
                            <h3 className="font-bold mb-2">Complete Profile</h3>
                            <p className="text-sm text-slate-500">Set your grade in Settings to calibrate your ST3 application target points.</p>
                        </div>
                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mb-4">2</div>
                            <h3 className="font-bold mb-2">Log Activities</h3>
                            <p className="text-sm text-slate-500">Add exams, publications, and teaching in the Portfolio tab to watch your points grow.</p>
                        </div>
                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mb-4">3</div>
                            <h3 className="font-bold mb-2">Track Readiness</h3>
                            <p className="text-sm text-slate-500">Use the Dashboard Radar to identify domain gaps before your next rotation.</p>
                        </div>
                    </div>
                </div>
            );

            default: return <Dashboard setActiveView={setActiveView} />;
        }
    };

    return (
        <>
            {/* FIXED BETA BANNER */}
            <div className="fixed top-0 left-0 w-full bg-[#fff3cd] text-[#856404] py-2.5 text-center text-xs md:text-sm font-bold border-b border-[#ffeeba] z-[10000] flex items-center justify-center gap-2 px-4">
                <AlertCircle size={16} /> 
                <span className="truncate">🚧 Beta: Report issues via Feedback!</span>
            </div>

            {/* Layout Container */}
            <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row font-sans text-slate-900 pt-[42px]">
                
                {/* MOBILE TOP NAV (Visible only on mobile) */}
                <div className="md:hidden flex items-center justify-between p-4 bg-slate-900 text-white sticky top-0 z-[9998]">
                    <h1 className="font-bold text-sm">Your Surgical Career</h1>
                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-1">
                        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {/* SIDEBAR (Adaptive) */}
                <aside className={`${isMobileMenuOpen ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-64 bg-slate-900 text-slate-300 h-[calc(100vh-42px)] md:h-screen sticky top-[42px] md:top-0 z-[9999] shadow-xl`}>
                    <div className="hidden md:block p-6 border-b border-slate-800">
                        <h1 className="text-lg font-bold text-white leading-tight">Your Surgical<br/>Career</h1>
                    </div>
                    
                    <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
                        {navItems.map((item) => (
                            <React.Fragment key={item.id}>
                                {item.separate && <div className="my-2 border-t border-slate-700 mx-2"></div>}
                                <button 
                                    onClick={() => { setActiveView(item.id); setIsMobileMenuOpen(false); }} 
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${activeView === item.id ? 'bg-blue-600 text-white' : 'hover:bg-slate-800'}`}
                                >
                                    <item.icon size={18} /> {item.label}
                                </button>
                            </React.Fragment>
                        ))}
                    </nav>

                    <div className="px-6 py-4 space-y-3 border-t border-slate-800">
                        <button onClick={() => { setActiveView('feedback'); setIsMobileMenuOpen(false); }} className={`flex items-center gap-3 text-xs font-medium transition-colors ${activeView === 'feedback' ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}>
                            <MessageSquare size={14} /> Feedback & Requests
                        </button>
                        <button onClick={() => { setActiveView('quick-start'); setIsMobileMenuOpen(false); }} className={`flex items-center gap-3 text-xs transition-colors ${activeView === 'quick-start' ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}>
                            <Info size={14} /> Quick Start Guide
                        </button>
                        <button onClick={() => { setActiveView('privacy'); setIsMobileMenuOpen(false); }} className={`flex items-center gap-3 text-xs transition-colors ${activeView === 'privacy' ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}>
                            <FileText size={14} /> Privacy Policy
                        </button>
                        <button onClick={() => { setActiveView('disclaimer'); setIsMobileMenuOpen(false); }} className={`flex items-center gap-3 text-xs transition-colors ${activeView === 'disclaimer' ? 'text-white font-bold' : 'text-slate-500 hover:text-slate-300'}`}>
                            <AlertCircle size={14} /> Disclaimer
                        </button>
                        <div className="pt-2 text-[10px] text-slate-600 uppercase tracking-widest font-semibold">
                            © 2026 Your Surgical Career
                        </div>
                    </div>

                    <div className="p-4 border-t border-slate-800">
                        <div className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 cursor-pointer group">
                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xs">{getInitials()}</div>
                            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{getDisplayName()}</p></div>
                            <button onClick={handleLogout} className="text-slate-500 hover:text-white transition-colors">
                                <LogOut size={16} />
                            </button>
                        </div>
                    </div>
                </aside>

                {/* MAIN CONTENT */}
                <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
                    <div className="flex-1 overflow-auto bg-slate-50">
                        <div className="max-w-7xl mx-auto w-full">
                            {renderContent()}
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}